import React from "react";
import {
  Alert,
  Backdrop,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

import ActiveContextSelector from "../../../components/ActiveContextSelector";
import { step1Styles } from "./contextStep.styles";

const ContextStepView = ({
  ROWS_PER_PAGE,
  authLoading,
  checkingSessions,
  fileInputRef,
  handleCloseSnackbar,
  handleCloseGenerateConfirm,
  handlePdfSelected,
  handleRemoveSelectedPdf,
  handleCloseSessionsBlocked,
  handleConfirmGenerarContexto,
  handleDescargarContexto,
  handleRequestContinue,
  handleRequestGenerarContexto,
  loading,
  loadingUnidades,
  nombreIE,
  onBack,
  onNext,
  openGenerateConfirm,
  openSnackbar,
  openSessionsBlocked,
  page,
  setPage,
  selectedPdfFile,
  snackbarMsg,
  snackbarType,
  unidades,
  userRole,
  willOverwriteExistingContext,
}) => {
  const totalPages = Math.ceil(unidades.length / ROWS_PER_PAGE);
  const unidadesPaginadas = unidades.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);
  const mostrarTabla = unidades.length > 0;
  const overlayOpen = authLoading || loading || checkingSessions || loadingUnidades;
  const overlayMessage = authLoading
    ? "Actualizando contexto institucional..."
    : loading
      ? "Analizando información institucional..."
      : checkingSessions
        ? "Validando sesiones existentes..."
        : "Cargando información institucional...";

  return (
    <Container maxWidth="xl" disableGutters sx={step1Styles}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "stretch" },
          gap: { xs: 3, md: 4, lg: 6 },
          px: { xs: 0, lg: 3 },
        }}
      >
        {mostrarTabla && (
          <Box className="step1-left" sx={{ flexShrink: 0 }}>
            <Box className="step1-table-card">
              <Box className="step1-table-header">
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5}}>
                  <MenuBookRoundedIcon sx={{ color: "#ffffff" }} />
                  <Typography variant="h6" sx={{ color: "#ffffff", fontWeight: 700, fontSize: "1.1rem" }}>
                    Unidades Registradas
                  </Typography>
                </Box>
                <Box className="step1-badge">{unidades.length} Total</Box>
              </Box>

              <TableContainer sx={{ maxHeight: { xs: 420, md: "none" }, overflowY: { xs: "auto", md: "visible" } }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width={60}>NRO</TableCell>
                      <TableCell>UNIDAD DE APRENDIZAJE</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {unidadesPaginadas.map((u, i) => (
                      <TableRow key={u.id} hover>
                        <TableCell sx={{ textAlign: "center", fontWeight: 600, color: "#8fa0b7", verticalAlign: "middle" }}>
                          {page * ROWS_PER_PAGE + i + 1}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: "normal", lineHeight: 1.4, fontWeight: 600, color: "#4a5568", verticalAlign: "middle" }}>
                          {u.nombreUnidad}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {totalPages > 1 && (
              <Box className="step1-pager">
                <Button size="small" disabled={page === 0} onClick={() => setPage((prev) => prev - 1)} className="step1-pager-btn">
                  {"<"}
                </Button>
                <Typography variant="body2" className="step1-pager-text">
                  {page + 1} de {totalPages}
                </Typography>
                <Button size="small" disabled={page + 1 >= totalPages} onClick={() => setPage((prev) => prev + 1)} className="step1-pager-btn">
                  {">"}
                </Button>
              </Box>
            )}
          </Box>
        )}

        <Box className="step1-right" sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch", minHeight: { md: "100%" } }}>
          <Box className="step1-right-card">
            <Box className="step1-context-wrapper">
              <ActiveContextSelector title="Contexto Institucional" />
            </Box>

            <Box>
              <Typography variant="h4" className="step1-title">
                Contexto institucional de la I.E. {nombreIE}
              </Typography>
              <Typography variant="body1" className="step1-subtitle">
                Genera automáticamente un análisis contextual que servirá como base para la planificación de unidades y sesiones de aprendizaje, alineadas a la realidad de tu institución.
              </Typography>
            </Box>

            <Box
              sx={{
                border: "1px dashed #cbd5e1",
                borderRadius: "14px",
                background: "#f8fafc",
                p: { xs: 2, md: 2.5 },
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
                <PictureAsPdfRoundedIcon sx={{ color: "#dc2626" }} />
                <Typography sx={{ color: "#24324a", fontWeight: 700 }}>
                  Insumo adicional en PDF
                </Typography>
              </Box>

              <Typography sx={{ color: "#64748b", lineHeight: 1.6, mb: 2 }}>
                Puedes adjuntar un documento PDF de referencia institucional. Su uso es opcional y servirá como insumo complementario para elaborar el contexto.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  gap: 1.5,
                }}
              >
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<UploadFileRoundedIcon />}
                  disabled={loading || checkingSessions}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                    borderColor: "#cbd5e1",
                    color: "#334155",
                    background: "#fff",
                    justifyContent: "flex-start",
                  }}
                >
                  {selectedPdfFile ? "Reemplazar PDF" : "Subir PDF"}
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    accept=".pdf,application/pdf"
                    onChange={handlePdfSelected}
                  />
                </Button>

                {selectedPdfFile && (
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={handleRemoveSelectedPdf}
                    disabled={loading || checkingSessions}
                    startIcon={<DeleteOutlineRoundedIcon />}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: "#64748b",
                      justifyContent: "flex-start",
                    }}
                  >
                    Quitar archivo
                  </Button>
                )}
              </Box>

              <Typography sx={{ color: "#94a3b8", fontSize: "0.8rem", mt: 1.5 }}>
                Formato permitido: PDF. Tamaño máximo: 5 MB.
              </Typography>

              {selectedPdfFile && (
                <Box sx={{ mt: 2 }}>
                  <Chip
                    icon={<PictureAsPdfRoundedIcon />}
                    label={selectedPdfFile.name}
                    variant="outlined"
                    sx={{
                      maxWidth: "100%",
                      borderRadius: "999px",
                      background: "#fff",
                      "& .MuiChip-label": {
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      },
                    }}
                  />
                </Box>
              )}
            </Box>

            <Box className="step1-primary-actions">
              <Button
                variant="contained"
                onClick={handleRequestGenerarContexto}
                disabled={loading || checkingSessions}
                startIcon={<AutoAwesomeRoundedIcon />}
                className="step1-btn step1-btn-generate"
              >
                {loading ? "Generando..." : checkingSessions ? "Validando..." : "Generar contexto"}
              </Button>

              <Button
                variant="outlined"
                startIcon={<DownloadRoundedIcon />}
                onClick={handleDescargarContexto}
                disabled={loading}
                className="step1-btn step1-btn-download"
              >
                Descargar (PDF)
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        className="step1-actions"
        sx={{
          justifyContent: userRole === "ADMIN" || userRole === "DIRECTOR" ? "space-between" : "flex-end",
        }}
      >
        {(userRole === "ADMIN" || userRole === "DIRECTOR") && (
          <Button variant="outlined" onClick={onBack} className="step1-btn step1-btn-back">
            Atrás
          </Button>
        )}

        <Button
          variant="contained"
          onClick={() => handleRequestContinue(onNext)}
          disabled={checkingSessions || loading}
          endIcon={<ChevronRightRoundedIcon />}
          className="step1-btn step1-btn-next"
        >
          Continuar
        </Button>
      </Box>

      <Backdrop
        open={overlayOpen}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 1,
          color: "#fff",
          backdropFilter: "blur(4px)",
          backgroundColor: "rgba(0, 0, 0, 0.55)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 2, fontWeight: 500 }}>{overlayMessage}</Typography>
        </Box>
      </Backdrop>

      <Snackbar open={openSnackbar} autoHideDuration={2000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarType} variant="filled" sx={{ width: "100%" }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>

      <Dialog
        open={openGenerateConfirm}
        onClose={handleCloseGenerateConfirm}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            fontSize: "1.3rem",
            color: "#24324a",
            pb: 1,
          }}
        >
          Confirmar generación de contexto
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography sx={{ color: "#475569", lineHeight: 1.7, mb: 2 }}>
            El proceso de generación de contexto puede tomar hasta 2 minutos.
          </Typography>
          {selectedPdfFile && (
            <Alert severity="info" sx={{ borderRadius: "10px", alignItems: "center", mb: 2 }}>
              Se utilizará el archivo <strong>{selectedPdfFile.name}</strong> como insumo complementario para elaborar el contexto institucional.
            </Alert>
          )}
          <Alert
            severity={willOverwriteExistingContext ? "warning" : "info"}
            sx={{ borderRadius: "10px", alignItems: "center" }}
          >
            {willOverwriteExistingContext
              ? "Si aceptas, este proceso va a sobreescribir el contexto institucional que ya fue generado anteriormente para esta institución."
              : "Si aceptas, el sistema volverá a generar el contexto institucional con la información más reciente disponible."}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={handleCloseGenerateConfirm}
            sx={{
              color: "#64748b",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmGenerarContexto}
            sx={{
              bgcolor: "#00bcd4",
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              "&:hover": { bgcolor: "#00a9b9" },
            }}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openSessionsBlocked}
        onClose={handleCloseSessionsBlocked}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            fontSize: "1.3rem",
            color: "#d32f2f",
            pb: 1,
          }}
        >
          No es posible generar un nuevo contexto
        </DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Typography sx={{ color: "#475569", lineHeight: 1.7, mb: 2 }}>
            Ya existe al menos una sesión creada para esta institución educativa.
          </Typography>
          <Alert severity="warning" sx={{ borderRadius: "10px", alignItems: "center" }}>
            Para generar un nuevo contexto y reiniciar esta planificación, primero debes revisar o eliminar las sesiones existentes desde el buscador.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            variant="contained"
            onClick={handleCloseSessionsBlocked}
            sx={{
              bgcolor: "#475569",
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              borderRadius: "8px",
              px: 3,
              "&:hover": { bgcolor: "#334155" },
            }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ContextStepView;
